import { ObjectShadow, SlideObject, SlideObjectStyle } from '../types';

export const DEFAULT_SHADOW: ObjectShadow = {
  enabled: false,
  color: '#111111',
  opacity: 0.4,
  angle: 135,
  distance: 8,
  blur: 12,
  spread: 0,
};

export function resolvedShadow(style?: SlideObjectStyle): ObjectShadow {
  const shadow = style?.shadow;
  return {
    ...DEFAULT_SHADOW,
    ...shadow,
    enabled: Boolean(shadow?.enabled),
    color: shadow?.color || DEFAULT_SHADOW.color,
    opacity: shadow?.opacity ?? DEFAULT_SHADOW.opacity,
    angle: shadow?.angle ?? DEFAULT_SHADOW.angle,
    distance: shadow?.distance ?? DEFAULT_SHADOW.distance,
    blur: shadow?.blur ?? DEFAULT_SHADOW.blur,
    spread: shadow?.spread ?? DEFAULT_SHADOW.spread,
  };
}

export function shadowOffsets(angle: number, distance: number): { x: number; y: number } {
  const rad = ((Number(angle) + 180) * Math.PI) / 180;
  return {
    x: Math.round(Math.cos(rad) * distance * 100) / 100,
    y: Math.round(-Math.sin(rad) * distance * 100) / 100,
  };
}

export function colorWithOpacity(hex: string, opacity: number): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  const value = match ? parseInt(match[1], 16) : 0x111111;
  const alpha = Math.max(0, Math.min(1, Number(opacity)));
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},${alpha})`;
}

export function canUseShadowSpread(object: SlideObject): boolean {
  if (object.type === 'text') return Boolean(object.style?.backgroundColor && object.style.backgroundColor !== 'transparent');
  const shape = object.style?.shape || 'rect';
  return object.type === 'shape' && (shape === 'rect' || shape === 'roundRect' || !object.style?.shape);
}

export function objectShadowCss(object: SlideObject): { boxShadow?: string; filter?: string } {
  const shadow = resolvedShadow(object.style);
  if (!shadow.enabled) return {};
  const { x, y } = shadowOffsets(shadow.angle, shadow.distance);
  const color = colorWithOpacity(shadow.color, shadow.opacity);
  if (canUseShadowSpread(object)) {
    return { boxShadow: `${x}px ${y}px ${shadow.blur}px ${shadow.spread}px ${color}` };
  }
  return { filter: `drop-shadow(${x}px ${y}px ${shadow.blur}px ${color})` };
}

export function objectBlendCss(style?: SlideObjectStyle): { mixBlendMode?: 'multiply' | 'normal' } {
  if (style?.blendMode === 'multiply') return { mixBlendMode: 'multiply' };
  return {};
}

export function objectEffectSx(object: SlideObject): Record<string, string | undefined> {
  const shadow = objectShadowCss(object);
  const blend = objectBlendCss(object.style);
  return {
    boxShadow: shadow.boxShadow,
    filter: shadow.filter,
    mixBlendMode: blend.mixBlendMode,
  };
}

export function objectEffectCss(object: SlideObject): string {
  const shadow = objectShadowCss(object);
  const blend = objectBlendCss(object.style);
  const parts: string[] = [];
  if (shadow.boxShadow) parts.push(`box-shadow:${shadow.boxShadow};`);
  if (shadow.filter) parts.push(`filter:${shadow.filter};`);
  if (blend.mixBlendMode) parts.push(`mix-blend-mode:${blend.mixBlendMode};`);
  return parts.join('');
}
